import { TemplateResult, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_PHASR_DETECTED_INCIDENTS } from 'apps/webcomponents/src/webcomponents.constants';
import {
  DetectedIncidentsTemplatesStringKeys,
  PhasrDetectedIncidentsPayload,
} from '@nirvana/shared/dtos/types/widgets/common';
import { GzGenericWidgetContent } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget';
import { WidgetSectionItemSeverity } from 'apps/webcomponents/src/ui/gzWidgetSectionItem/types';
import { IncidentStringItem } from '../types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { desanitizeObject } from 'apps/webcomponents/src/utils/sanitization.utils';
import { isEmpty } from 'lodash';
import { WidgetStateFrontEnd } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget/types';
import { classMap } from 'lit/directives/class-map.js';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { INCIDENT_TYPE_EDR } from '@nirvana/shared/models/nestjs';
import { ColumnKeys } from '../../../../edr/incidentsGrid/types';

@customElement(WEB_COMPONENT_PHASR_DETECTED_INCIDENTS)
export class PhasrDetectedIncidentsWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: PhasrDetectedIncidentsPayload;

  protected strings: IncidentStringItem[] = [
    {
      key: DetectedIncidentsTemplatesStringKeys.LIVING_OFF_THE_LAND_BINARIES,
      name: translateFn('Living off the land binaries'),
    },
    {
      key: DetectedIncidentsTemplatesStringKeys.REMOTE_ADMIN_TOOLS,
      name: translateFn('Remote admin tools'),
    },
    {
      key: DetectedIncidentsTemplatesStringKeys.TAMPERING_TOOLS,
      name: translateFn('Tampering tools'),
    },
    {
      key: DetectedIncidentsTemplatesStringKeys.HACKTOOLS,
      name: translateFn('Piracy tools'),
    },
    {
      key: DetectedIncidentsTemplatesStringKeys.MINERS,
      name: translateFn('Crypto miners'),
    },
  ];

  static get styles() {
    return styles;
  }

  protected renderContent(): TemplateResult {
    return html`${this.renderTotalIncidentsNumber()}${this.strings.map(item => this.renderItem(item))}`;
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-section"><gz-loader rows="1" class="mb-2"></gz-loader></div>
      <div class="loading-section"><gz-loader rows="5"></gz-loader></div>
    `;
  }

  private renderTotalIncidentsNumber(): TemplateResult {
    const totalIncidents = this.getTotalIncidents();
    const displayValue = totalIncidents === -1 ? '--' : totalIncidents;

    return html`
      <gz-widget-section-item
        class="mb-1 mt-3 total-incidents"
        .name=${translateFn('TOTAL INCIDENTS')}
        .value=${displayValue}
        .severity=${WidgetSectionItemSeverity.NONE}
        .state=${WidgetStateFrontEnd.IS_ACTIVE}
      />
    `;
  }

  private getTotalIncidents(): number {
    if (this.isErrorOrEmptyState() || isEmpty(this.payload)) {
      return -1;
    }

    return Object.values(this.payload).reduce((total, value) => {
      return typeof value === 'number' ? total + value : total;
    }, 0);
  }

  private isErrorOrEmptyState(): boolean {
    return this.state === WidgetStateFrontEnd.IS_ERROR || this.state === WidgetStateFrontEnd.IS_EMPTY;
  }

  private renderItem(item: IncidentStringItem): TemplateResult {
    const incident = desanitizeObject(item);
    const value = this.getIncidentValue(incident);
    const valueSeverity = this.getItemSeverity(value);
    const severity = value !== '--' ? valueSeverity : WidgetSectionItemSeverity.EMPTY;
    const state = this.getIncidentState();
    const tooltipText = this.getTooltipText(severity);

    return html`
      <gz-widget-section-item
        .name=${incident.name}
        .value=${value}
        class=${classMap({
          'disabled-item': value === '--',
          pointer: severity !== WidgetSectionItemSeverity.EMPTY && typeof value === 'number' && value > 0,
        })}
        .severity=${severity}
        .state=${state}
        .itemTooltipText=${tooltipText}
        @click=${() => this.handleIncidentClick(value, incident.key)}
      />
    `;
  }

  private getItemSeverity(value: number | string): WidgetSectionItemSeverity {
    return typeof value === 'number' && value > 0 ? WidgetSectionItemSeverity.ERROR : WidgetSectionItemSeverity.SUCCESS;
  }

  /**
   * Handle incident click
   * @param value - The value of the incident, either a number or a string
   * @param category - The category of the incident as a string
   * @private
   */
  private handleIncidentClick(value: number | string, category: string): void {
    if (value === '--' || (typeof value === 'number' && value < 1)) {
      return;
    }

    const params = {
      filters: [
        {
          [ColumnKeys.COMPANY] : {
            in: [this.payload.companyId]
          }
        },
        {
          [ColumnKeys.PHASR_CATEGORIES] : {
            in : [category]
          }
        },
        {
          [ColumnKeys.TYPE] : {
            in : [INCIDENT_TYPE_EDR]
          }
        }
      ],
    };
    Application.lib.Navigation.openAppPageInNewTab('/incidents/filter/nodeCategories/', params);
  }

  private getTooltipText(severity: WidgetSectionItemSeverity): string {
    switch (severity) {
      case WidgetSectionItemSeverity.ERROR:
        return translateFn('Processes from this category involved in incidents.');
      case WidgetSectionItemSeverity.SUCCESS:
        return translateFn('No processes from this category involved in incidents.');
      case WidgetSectionItemSeverity.EMPTY:
        return translateFn('No data available.');
      default:
        return '';
    }
  }

  private getIncidentState(): WidgetStateFrontEnd {
    return this.isErrorOrEmptyState() ? WidgetStateFrontEnd.IS_ACTIVE : this.state;
  }

  private getIncidentValue(incident: IncidentStringItem): number | string {
    if (this.isErrorOrEmptyState()) {
      return '--';
    }

    return !isEmpty(this.payload) && this.payload[incident.key] !== undefined ? this.payload[incident.key] : '--';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'phasr-detected-incidents': PhasrDetectedIncidentsWebComponent;
  }
}
